import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTitle } from '../../../context/TitleContext';
import { motion } from 'framer-motion';
import { FaArrowLeft } from 'react-icons/fa';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorEmptyDays = () => {
    const { clinicId } = useParams();
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    
    const [emptyDays, setEmptyDays] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchEmptyDays = useCallback(async () => {
        try {
            setLoading(true);
            
            const response = await apiClient.get(`/doctor_dash/empty_work_days?clinic_id=${clinicId}`);
            
            const data = response.data?.result || response.data?.data || response.data || [];
            setEmptyDays(Array.isArray(data) ? data : []);
        } catch (error) {
            showErrorToast('فشل في جلب الأيام الفارغة');
        } finally {
            setLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        setTitle('الغير نشطة');
        fetchEmptyDays();
    }, [setTitle, clinicId, fetchEmptyDays]);

    const getDayNameInArabic = (day) => {
        const daysMap = {
            saturday: 'السبت',
            sunday: 'الأحد',
            monday: 'الإثنين',
            tuesday: 'الثلاثاء',
            wednesday: 'الأربعاء',
            thursday: 'الخميس',
            friday: 'الجمعة'
        };
        return daysMap[day] || day;
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">الغير نشطة</h4>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/doctor/clinics')}
                    >
                        <FaArrowLeft className="mx-2" />
                        رجوع
                    </button>
                </div>

                {/* Empty Days */}
                <div className="card shadow-sm">
                    <div className="card-body">
                        {emptyDays.length === 0 ? (
                            <p className="text-muted text-center">لا توجد أيام</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-empty-days">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">اليوم</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {emptyDays.map((day, index) => (
                                            <motion.tr
                                                key={day.id || day.day_id || index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                className="table-row-empty-days"
                                            >
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #{index + 1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span>{getDayNameInArabic(day.day)}</span>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoctorEmptyDays;

// CSS for table styling
const style = document.createElement('style');
style.textContent = `
    .table-empty-days {
        border-collapse: separate;
        border-spacing: 0;
    }
    
     .table-empty-days th {
         background: transparent;
         color: #374151;
         font-weight: 600;
         border: none;
         padding: 8px;
         font-size: 0.9rem;
         text-align: center;
     }
     
     .table-empty-days td {
         padding: 8px;
         border-bottom: 1px solid #e9ecef;
         vertical-align: middle;
         text-align: center;
     }
    
    .table-row-empty-days:hover {
        background-color: rgba(111, 66, 193, 0.08);
        transform: translateY(-1px);
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
    }
    
     .order-number-badge {
         background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
         color: white;
         padding: 0.3rem 0.6rem;
         border-radius: 15px;
         font-weight: 600;
         font-size: 0.75rem;
         display: inline-block;
         min-width: 40px;
         text-align: center;
         box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
     }
`;
// Only append if not already added
if (!document.getElementById('doctor-empty-days-style')) {
    style.id = 'doctor-empty-days-style';
    document.head.appendChild(style);
}

